import React from 'react';
import { Check } from 'lucide-react';

const BULLETS = [
  'FIFO cost basis from your real sell transactions',
  'Automatic 50% discount on assets held 12+ months',
  'Capital losses offset before the discount applies',
];

const GREEN  = '#6db981';
const BRONZE = '#d4b483';

// Static example CGT summary cards (AUD), mirrors the real Tax tab.
const CARDS: { label: string; value: string; sub: string; color?: string }[] = [
  { label: 'Net capital gain',     value: 'A$28,450', sub: 'after losses & discount', color: GREEN },
  { label: 'CGT discount applied', value: 'A$14,225', sub: '50% on eligible gains' },
  { label: 'Taxable gain',         value: 'A$14,225', sub: 'assessed for CGT' },
  { label: 'Est. CGT owed',        value: 'A$4,623',  sub: 'at 32.5% marginal', color: BRONZE },
];

export function CgtSection() {
  return (
    <section
      className="px-6 py-24 relative"
      style={{
        borderTop: '0.5px solid rgba(255,255,255,0.06)',
        background:
          'linear-gradient(180deg, rgba(212,180,131,0.04), transparent 60%)',
      }}
    >
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-12 items-center">
        {/* LEFT, 2/5 - copy */}
        <div className="md:col-span-2">
          <div
            className="text-[11px] font-semibold tracking-[0.18em] mb-4"
            style={{ color: '#d4b483' }}
          >
            AUSTRALIAN CGT
          </div>
          <h2
            className="font-semibold"
            style={{
              color: '#f4f1ea',
              fontSize: 'clamp(26px, 4vw, 36px)',
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
            }}
          >
            Estimate your capital gains tax.
          </h2>
          <p
            className="mt-4 text-[15px] leading-relaxed"
            style={{ color: 'rgba(244,241,234,0.6)' }}
          >
            See the capital gains tax on what you&apos;ve actually sold
            this financial year, built around Australian CGT rules, for planning
            ahead.
          </p>

          <ul className="mt-6 space-y-3">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span
                  className="flex items-center justify-center rounded-full mt-0.5"
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: 'rgba(212,180,131,0.15)',
                  }}
                >
                  <Check className="w-3 h-3" style={{ color: '#d4b483' }} strokeWidth={3} />
                </span>
                <span className="text-[14px]" style={{ color: 'rgba(244,241,234,0.8)' }}>
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT, 3/5 - static mock card */}
        <div className="md:col-span-3">
          <div
            className="rounded-xl"
            style={{
              backgroundColor: '#131313',
              border: '0.5px solid rgba(255,255,255,0.08)',
              padding: '18px',
            }}
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[13px] font-medium" style={{ color: '#f4f1ea' }}>
                Estimated capital gains tax
              </div>
              <div
                className="text-[10px] tracking-[0.14em]"
                style={{ color: 'rgba(244,241,234,0.45)' }}
              >
                FY 2024–25
              </div>
            </div>

            {/* Four summary cards */}
            <div className="grid grid-cols-2 gap-2.5">
              {CARDS.map((c) => (
                <div
                  key={c.label}
                  className="rounded-lg p-3"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: '0.5px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <div
                    className="uppercase"
                    style={{ fontSize: 9, letterSpacing: '0.08em', color: 'rgba(244,241,234,0.45)' }}
                  >
                    {c.label}
                  </div>
                  <div
                    className="mt-1 text-[18px] font-semibold tabular-nums"
                    style={{ color: c.color ?? '#f4f1ea' }}
                  >
                    {c.value}
                  </div>
                  <div className="text-[10px] mt-0.5" style={{ color: 'rgba(244,241,234,0.4)' }}>
                    {c.sub}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mt-3 text-[10px] tracking-wider"
              style={{ color: 'rgba(244,241,234,0.4)' }}
            >
              BASED ON YOUR ACTUAL SELL TRANSACTIONS
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
